import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Users, School, AlertCircle } from 'lucide-react';

import Button from '../../components/ui/Button';
import RegistrationProgressIndicator from '../../components/ui/RegistrationProgressIndicator';
import StatusIndicatorBadge from '../../components/ui/StatusIndicatorBadge';
import AuthenticationGuard from '../../components/ui/AuthenticationGuard';
import DeclarationPreview from './components/DeclarationPreview';
import SignedDeclarationUpload from './components/SignedDeclarationUpload';
import RegistrationChecklist from './components/RegistrationChecklist';
import CaptainSelection from './components/CaptainSelection';
import FinalSubmissionPanel from './components/FinalSubmissionPanel';
import { registrationService } from '../../services/registrationService';
import { supabase } from '../../lib/supabase';
import TeamOverviewTab from '../registration-detail-view/components/TeamOverviewTab';
import PlayerDetailsTab from '../registration-detail-view/components/PlayerDetailsTab';
import DocumentsTab from '../registration-detail-view/components/DocumentsTab';

const DeclarationAndSubmission = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [uploadedDeclaration, setUploadedDeclaration] = useState(null);
  const [selectedCaptain, setSelectedCaptain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationId, setRegistrationId] = useState(null);
  const [declarationData, setDeclarationData] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [activeTab, setActiveTab] = useState('declaration');
  const [teamData, setTeamData] = useState(null);
  const [playerData, setPlayerData] = useState([]);
  const [documentData, setDocumentData] = useState([]);
  const [isResubmission, setIsResubmission] = useState(false);

  // Load registration data from Supabase
  useEffect(() => {
    const loadRegistrationData = async () => {
      try {
        setLoading(true);
        // Clear previous data to ensure fresh load
        setDeclarationData(null);
        setRegistrationData(null);
        setUploadedDeclaration(null);
        setSelectedCaptain(null);
        setTeamData(null);
        setPlayerData([]);
        setDocumentData([]);
        
        const mobileNumber = localStorage.getItem('userMobile') || '';
        
        // Get registration ID from navigation state or from draft
        let regId = location?.state?.registrationId;
        const isViewModeFromState = location?.state?.viewMode;
        
        if (!regId) {
          // If in viewMode but no registrationId, navigate to dashboard
          if (isViewModeFromState) {
            navigate('/registration-dashboard');
            return;
          }
          
          // Get the current draft registration
          const draft = await registrationService.getDraftByMobile(mobileNumber);
          if (!draft) {
            navigate('/registration-dashboard');
            return;
          }
          regId = draft.id;
        }

        setRegistrationId(regId);
        console.log('Loading registration data for ID:', regId);

        // Fetch registration details - this includes schools, coaches, and players
        // We fetch separately as well to ensure we have the latest data from Supabase
        const registration = await registrationService.getRegistrationDetails(regId);
        console.log('Registration details fetched:', {
          id: registration?.id,
          captain_player_id: registration?.captain_player_id,
          nestedPlayersCount: registration?.players?.length,
          nestedPlayers: registration?.players
        });
        
        // Check if we're in viewMode (from navigation state or if registration is approved/rejected)
        const isViewMode = location?.state?.viewMode || registration?.status === 'approved' || registration?.status === 'rejected';
        setViewMode(isViewMode);
        
        // Log registration data for debugging
        console.log('Registration data from Supabase:', {
          id: registration?.id,
          status: registration?.status,
          rejection_reason: registration?.rejection_reason,
          registration_number: registration?.registration_number,
          school_name: registration?.schools?.name,
          age_category: registration?.age_category,
          poc_name: registration?.poc_name,
          mobile_number: registration?.mobile_number
        });
        
        // Fetch players separately to ensure we have the latest data from Supabase
        const playersData = await registrationService.getPlayersByRegistration(regId);
        const players = Array.isArray(playersData) ? playersData : [];
        console.log('Players data from Supabase:', {
          registrationId: regId,
          playersData,
          playersArray: players,
          playersCount: players.length,
          firstPlayer: players[0],
          allPlayers: players
        });
        
        // Fetch coaches separately to ensure we have the latest data from Supabase
        const coachesData = await registrationService.getCoachesByRegistration(regId);
        const coaches = Array.isArray(coachesData) ? coachesData : [];
        console.log('Coaches data from Supabase:', coaches.length, 'coaches');
        const primaryCoach = coaches?.find(c => c.is_primary);
        const secondaryCoach = coaches?.find(c => !c.is_primary);

        // Check if captain is already selected - check both captain_player_id and is_captain flag
        let captain = null;
        let captainPlayerId = null;
        
        if (registration?.captain_player_id) {
          // First check captain_player_id from registration
          captain = players?.find(p => p.id === registration.captain_player_id);
          if (captain) {
            captainPlayerId = registration.captain_player_id;
          }
        }
        if (!captain) {
          // Fallback to checking is_captain flag on players
          captain = players?.find(p => p.is_captain);
          if (captain) {
            captainPlayerId = captain.id;
          }
        }
        
        if (captain) {
          setSelectedCaptain(captain.id);
          // Ensure captain_player_id is set if it's missing (sync issue fix)
          if (registration?.captain_player_id !== captain.id) {
            try {
              await registrationService.setCaptain(regId, captain.id);
              captainPlayerId = captain.id; // Update after sync
            } catch (error) {
              console.error('Error syncing captain_player_id:', error);
            }
          } else {
            captainPlayerId = registration.captain_player_id;
          }
        }

        // Check if signed declaration is already uploaded
        if (registration.declaration_uploaded && registration.declaration_file_path) {
          try {
            const declarationUrl = await registrationService.getSignedDeclarationUrl(regId);
            if (declarationUrl) {
              // Extract file name from path
              const fileName = registration.declaration_file_path.split('/').pop();
              setUploadedDeclaration({
                name: fileName,
                size: 0, // Size not stored, will show as unknown
                uploadDate: registration.updated_at ? new Date(registration.updated_at).toLocaleDateString('en-IN') : 'N/A',
                url: declarationUrl,
                filePath: registration.declaration_file_path
              });
            }
          } catch (error) {
            console.error('Error loading declaration URL:', error);
          }
        }

        // Transform players for display - use the separately fetched players (latest from Supabase)
        // Note: registration.players might be from nested query, but we use separately fetched players for accuracy
        // IMPORTANT: Mark captain based on both is_captain flag AND captain_player_id to ensure consistency
        const transformedPlayers = (players || []).map(player => {
          // Check if this player is the captain using multiple sources of truth
          const isCaptain = player.is_captain || 
                           player.id === captainPlayerId || 
                           player.id === registration?.captain_player_id ||
                           (captain && player.id === captain.id);
          
          return {
            id: player.id,
            name: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
            dob: player.date_of_birth ? new Date(player.date_of_birth).toLocaleDateString('en-GB') : 'N/A',
            role: player.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Player',
            mcaId: player.mca_id || 'N/A',
            isCaptain: isCaptain,
            photo: null,
            photoAlt: `Player ${player.first_name} ${player.last_name}`
          };
        });
        
        console.log('Transformed players for display:', {
          count: transformedPlayers?.length,
          players: transformedPlayers,
          playersWithCaptainFlag: transformedPlayers?.filter(p => p.isCaptain)
        });
        console.log('Captain info:', { 
          captainPlayerId, 
          selectedCaptain: captain?.id,
          captainName: captain?.first_name + ' ' + captain?.last_name,
          registrationCaptainId: registration?.captain_player_id,
          captainInTransformed: transformedPlayers?.find(p => p.isCaptain)?.name,
          captainInTransformedId: transformedPlayers?.find(p => p.isCaptain)?.id
        });

        // Build declaration data - use all fields from registration (latest from Supabase)
        const declData = {
          schoolName: registration.schools?.name || registration.school_name || 'N/A',
          schoolEmail: registration.school_email || 'N/A',
          ageCategory: registration.age_category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
          registrationDate: registration.created_at ? new Date(registration.created_at).toLocaleDateString('en-GB') : 'N/A',
          registrationNumber: registration.registration_number || registration.id || 'N/A',
          primaryCoach: primaryCoach ? {
            name: primaryCoach.name,
            mobile: registration.mobile_number || 'N/A',
            email: registration.poc_email || 'N/A'
          } : null,
          secondaryCoach: secondaryCoach ? {
            name: secondaryCoach.name,
            mobile: 'N/A',
            email: 'N/A'
          } : null,
          players: transformedPlayers
        };

        console.log('Declaration data built:', declData);
        setDeclarationData(declData);

        // Build registration data for submission - include all relevant fields from Supabase
        const regData = {
          id: registration.id,
          registrationNumber: registration.registration_number || registration.id,
          schoolName: registration.schools?.name || registration.school_name || 'N/A',
          ageCategory: registration.age_category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
          playerCount: players?.length || 0,
          status: registration.status || 'draft',
          rejectionReason: registration.rejection_reason || null
        };

        console.log('Registration data built:', regData);
        console.log('Registration status:', registration.status);
        console.log('ViewMode:', isViewMode);
        setRegistrationData(regData);

        // Check if this is a resubmission (draft status but was previously submitted/rejected)
        // Indicated by: has registration_number or submitted_at but status is now draft
        const isResubmissionCheck = registration.status === 'draft' && 
          (registration.registration_number || registration.submitted_at) &&
          !registration.declaration_uploaded;
        setIsResubmission(isResubmissionCheck);

        // If in viewMode, fetch additional data for tabs
        if (isViewMode) {
          // Calculate completion percentage
          const calculateAge = (dateOfBirth) => {
            if (!dateOfBirth) return null;
            const today = new Date();
            const birthDate = new Date(dateOfBirth);
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            return age;
          };

          const formatAgeCategory = (category) => {
            const map = {
              'boys_u14': 'Boys U14',
              'boys_u15': 'Boys U15',
              'boys_u16': 'Boys U16',
              'girls_u14': 'Girls U14',
              'girls_u15': 'Girls U15',
              'girls_u16': 'Girls U16'
            };
            return map[category] || category;
          };

          // Build team data for Team Overview tab
          const teamOverviewData = {
            schoolName: registration.schools?.name || registration.school_name || 'N/A',
            schoolAddress: registration.schools?.address || '',
            schoolContact: registration.schools?.phone || '',
            schoolEmail: registration.school_email || registration.schools?.email || '',
            pocName: registration.poc_name || '',
            pocPhone: registration.mobile_number || '',
            pocEmail: registration.poc_email || '',
            coachName: primaryCoach?.name || '',
            coachPhone: '', // Coaches don't have phone in current schema
            coachEmail: '', // Coaches don't have email in current schema
            coachExperience: '', // Not in current schema
            category: formatAgeCategory(registration.age_category),
            city: registration.city || registration.schools?.city || '',
            totalPlayers: players?.length || 0,
            completionPercentage: registrationService.calculateCompletionPercentage({
              ...registration,
              players: players || []
            }),
            submittedDate: registration.submitted_at || registration.created_at,
            lastUpdated: registration.updated_at || registration.created_at
          };
          setTeamData(teamOverviewData);

          // Build player data for Player Details tab
          const allPlayerDocuments = [];
          const transformedPlayers = await Promise.all((players || []).map(async (player) => {
            // Fetch documents for each player
            const playerDocs = await registrationService.getPlayerDocuments(player.id);
            
            // Generate signed URLs for all documents in parallel
            const docPromises = playerDocs.map(async (doc) => {
              try {
                const fileName = doc.file_path.replace('player-documents/', '');
                const { data: urlData, error: urlError } = await supabase.storage
                  .from('player-documents')
                  .createSignedUrl(fileName, 3600); // 1 hour expiration
                
                if (urlError) {
                  console.error(`Error creating signed URL for ${doc.file_path}:`, urlError);
                }
                
                return {
                  id: doc.id,
                  playerId: player.id,
                  playerName: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
                  name: `${doc.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Document'} - ${player.first_name} ${player.last_name}`,
                  type: doc.document_type,
                  fileUrl: urlData?.signedUrl || null,
                  filePath: doc.file_path,
                  fileSize: doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
                  uploadedDate: doc.uploaded_at,
                  status: doc.status || 'approved',
                  quality: doc.quality || 'medium',
                  reviewedBy: doc.reviewed_by || null,
                  reviewedDate: doc.reviewed_at || null,
                  thumbnail: urlData?.signedUrl || null
                };
              } catch (error) {
                console.error('Error getting document URL:', error);
                return null;
              }
            });
            
            // Wait for all signed URLs for this player's documents
            const processedDocs = await Promise.all(docPromises);
            allPlayerDocuments.push(...processedDocs.filter(doc => doc !== null));

            const validDocs = allPlayerDocuments.filter(d => d.playerId === player.id);

            return {
              id: player.id,
              name: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
              dateOfBirth: player.date_of_birth,
              age: calculateAge(player.date_of_birth),
              role: player.is_captain ? 'Captain' : (player.role?.replace('_', ' ') || 'Player'),
              mcaId: player.mca_id || 'N/A',
              phone: player.mobile_number || '',
              email: player.email || '',
              address: '', // Not in current schema
              validationStatus: 'verified', // Default
              documentsComplete: validDocs.length >= 4
            };
          }));

          setPlayerData(transformedPlayers);
          setDocumentData(allPlayerDocuments);
        }
      } catch (error) {
        console.error('Error loading registration data:', error);
        navigate('/registration-dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadRegistrationData();
  }, [navigate, location?.state?.registrationId]);

  // Sync declarationData players when selectedCaptain changes to ensure captain flag is correct
  useEffect(() => {
    if (declarationData && selectedCaptain) {
      console.log('Syncing captain flag in declarationData for selectedCaptain:', selectedCaptain);
      setDeclarationData(prev => {
        const updatedPlayers = prev.players?.map(player => {
          const isCaptain = player.id === selectedCaptain || player.isCaptain;
          if (isCaptain && !player.isCaptain) {
            console.log('Marking player as captain:', player.name, player.id);
          }
          return {
            ...player,
            isCaptain: isCaptain
          };
        }) || [];
        
        const captainInList = updatedPlayers.find(p => p.isCaptain);
        console.log('Captain in updated list:', captainInList?.name, captainInList?.id);
        
        return {
          ...prev,
          players: updatedPlayers
        };
      });
    }
  }, [selectedCaptain]); // Only depend on selectedCaptain to avoid infinite loops

  // Memoize checklist items to prevent unnecessary re-renders
  const checklistItems = useMemo(() => {
    if (!declarationData || !registrationData) return [];

    const captainName = declarationData?.players?.find((p) => p?.id === selectedCaptain)?.name || '';

    return [
      {
        id: 1,
        title: "Team Registration",
        description: "School and coach details submitted",
        status: "completed",
        details: "Completed"
      },
      {
        id: 2,
        title: "Player Registration",
        description: "All players registered with complete details",
        status: "completed",
        details: `${registrationData.playerCount} players registered`
      },
      {
        id: 3,
        title: "Document Upload",
        description: "All required documents uploaded for each player",
        status: "completed",
        details: "Documents uploaded"
      },
      {
        id: 4,
        title: "Captain Selection",
        description: "Designate a team captain from registered players",
        status: selectedCaptain ? "completed" : "pending",
        details: selectedCaptain ? `Captain: ${captainName}` : "Please select a captain",
        action: () => document.getElementById('captain-section')?.scrollIntoView({ behavior: 'smooth' })
      },
      {
        id: 5,
        title: "Declaration Review",
        description: "Review auto-generated declaration document",
        status: "completed",
        details: "Declaration generated successfully"
      },
      {
        id: 6,
        title: "Signed Declaration Upload",
        description: "Upload signed and stamped declaration PDF",
        status: uploadedDeclaration ? "completed" : "pending",
        details: uploadedDeclaration ? `Uploaded: ${uploadedDeclaration?.name}` : "Awaiting signed declaration",
        action: () => document.getElementById('upload-section')?.scrollIntoView({ behavior: 'smooth' })
      }
    ];
  }, [selectedCaptain, uploadedDeclaration, declarationData, registrationData]);

  // Memoize progress steps to prevent unnecessary re-renders
  const progressSteps = useMemo(() => {
    if (!checklistItems || checklistItems.length === 0) return [];

    return checklistItems.map((item, index) => {
      const isCompleted = item.status === 'completed';
      // Find the first pending item to determine current step
      const firstPendingIndex = checklistItems.findIndex(i => i.status === 'pending');
      const isActive = !isCompleted && index === firstPendingIndex;

      return {
        id: item.id,
        label: item.title,
        completed: isCompleted,
        active: isActive
      };
    });
  }, [checklistItems]);

  const handleDownloadDeclaration = async () => {
    if (!declarationData) {
      alert('Declaration data not loaded. Please wait and try again.');
      return;
    }

    try {
      // Import PDF libraries dynamically
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      // Get the declaration preview element
      const element = document.querySelector('.declaration-preview-content');
      if (!element) {
        alert('Declaration preview not found. Please refresh the page and try again.');
        return;
      }

      // Show loading message
      const loadingMessage = document.createElement('div');
      loadingMessage.textContent = 'Generating PDF...';
      loadingMessage.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.8); color: white; padding: 20px; border-radius: 8px; z-index: 10000;';
      document.body.appendChild(loadingMessage);

      // Temporarily reset zoom for better PDF quality
      const originalWidth = element.style.width;
      element.style.width = '100%';

      // Generate canvas from HTML
      const canvas = await html2canvas(element, {
        scale: 1.5, // Reduced from 2 to 1.5 for smaller file size while maintaining quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Restore original width
      element.style.width = originalWidth;

      // Remove loading message
      document.body.removeChild(loadingMessage);

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position = 0;

      // Add first page
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate filename
      const schoolName = declarationData?.schoolName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Registration';
      const category = declarationData?.ageCategory?.replace(/[^a-zA-Z0-9]/g, '_') || 'Declaration';
      const filename = `MI_Junior_Cricket_Declaration_${schoolName}_${category}_${new Date().getTime()}.pdf`;

      // Download PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again or use your browser\'s print function (Ctrl+P / Cmd+P).');
    }
  };

  const handleFileUpload = (file) => {
    setUploadedDeclaration(file);
  };

  const handleUploadComplete = (result) => {
    console.log('Declaration uploaded successfully:', result);
    // Optionally reload registration data to get updated status
  };

  const handleUploadError = (error) => {
    console.error('Declaration upload error:', error);
    alert(`Failed to upload declaration: ${error.message || 'Unknown error'}`);
  };

  const handleRemoveFile = async () => {
    if (!registrationId) {
      setUploadedDeclaration(null);
      return;
    }

    // Confirm removal
    const confirmed = window.confirm('Are you sure you want to remove the signed declaration? You can upload it again before submitting.');
    if (!confirmed) {
      return;
    }

    try {
      // Remove declaration from storage and database
      await registrationService.removeSignedDeclaration(registrationId);
      
      // Clear local state
      setUploadedDeclaration(null);
      
      console.log('Declaration removed successfully');
    } catch (error) {
      console.error('Error removing declaration:', error);
      alert(`Failed to remove declaration: ${error.message || 'Unknown error'}`);
      // Don't clear local state if removal failed
    }
  };

  const handleCaptainSelect = useCallback(async (captain) => {
    if (!registrationId || !captain?.id) {
      console.error('Missing registrationId or captain.id:', { registrationId, captain });
      alert('Invalid captain selection. Please try again.');
      return;
    }
    
    try {
      console.log('Setting captain:', { registrationId, playerId: captain.id });
      
      // Save captain to database - this will update both is_captain and captain_player_id
      const result = await registrationService.setCaptain(registrationId, captain.id);
      
      if (!result) {
        throw new Error('setCaptain returned no data');
      }
      
      console.log('Captain set successfully, refreshing players data...');
      
      // Refresh players data to get updated captain status
      const updatedPlayers = await registrationService.getPlayersByRegistration(registrationId);
      
      if (!updatedPlayers || updatedPlayers.length === 0) {
        console.warn('No players found after captain selection');
      }
      
      // Rebuild transformed players with updated captain status
      const updatedTransformedPlayers = (updatedPlayers || []).map(player => ({
        id: player.id,
        name: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
        dob: player.date_of_birth ? new Date(player.date_of_birth).toLocaleDateString('en-GB') : 'N/A',
        role: player.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Player',
        mcaId: player.mca_id || 'N/A',
        isCaptain: player.is_captain || false,
        photo: null,
        photoAlt: `Player ${player.first_name} ${player.last_name}`
      }));
      
      // Verify captain is in the updated list
      const selectedCaptainInList = updatedTransformedPlayers.find(p => p.id === captain.id);
      if (!selectedCaptainInList) {
        console.error('Selected captain not found in updated players list');
      } else if (!selectedCaptainInList.isCaptain) {
        console.error('Selected captain does not have is_captain flag set');
      }
      
      // Update declaration data with refreshed players
      setDeclarationData(prev => ({
        ...prev,
        players: updatedTransformedPlayers
      }));
      
      // Update selectedCaptain state
      setSelectedCaptain(captain.id);
      
      console.log('Captain selection completed successfully');
    } catch (error) {
      console.error('Error setting captain:', error);
      alert(`Failed to save captain selection: ${error?.message || 'Unknown error'}. Please try again.`);
    }
  }, [registrationId]);

  const handleFinalSubmit = async (registrationId) => {
    try {
      if (!registrationId) {
        alert('Registration ID is missing. Please try again.');
        return;
      }

      // Submit the registration to Supabase
      const submittedRegistration = await registrationService.submitRegistration(registrationId);
      
      console.log('Registration submitted successfully:', submittedRegistration);
      
      // Update local state with submitted registration data
      if (submittedRegistration) {
        setRegistrationData(prev => ({
          ...prev,
          status: 'submitted',
          registrationNumber: submittedRegistration.registration_number
        }));
      }
      
      // Don't navigate here - let the FinalSubmissionPanel show success modal first
      // The modal will handle navigation
    } catch (error) {
      console.error('Error submitting registration:', error);
      throw error; // Re-throw to let FinalSubmissionPanel handle the error display
    }
  };

  const isReadyToSubmit = selectedCaptain && uploadedDeclaration;

  return (
    <AuthenticationGuard>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-30 shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowLeft"
                  onClick={() => {
                    console.log('Navigating back with registrationId:', registrationId);
                    navigate(viewMode ? '/registration-dashboard' : '/document-upload-center', { state: { registrationId } });
                  }}
                  aria-label="Go back" />

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h1 className="text-xl lg:text-2xl font-bold text-foreground">
                      {viewMode && registrationData?.status === 'rejected' 
                        ? 'Rejected Registration Details' 
                        : viewMode 
                          ? 'Registration Details' 
                          : 'Declaration & Submission'}
                    </h1>
                    {registrationData?.status && (
                      <StatusIndicatorBadge status={registrationData.status} showIcon={true} />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {viewMode ? 'View your registration details' : 'Final step - Review and submit your registration'}
                  </p>
                </div>
              </div>
              <div className="hidden lg:flex items-center gap-3">
                {!viewMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Save"
                    onClick={() => alert('Draft saved successfully')}>
                    Save Draft
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Home"
                  onClick={() => navigate('/registration-dashboard')}>
                  Dashboard
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Rejection Banner */}
        {viewMode && registrationData?.status === 'rejected' && registrationData?.rejectionReason && (
          <div className="container mx-auto px-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Registration Rejected</h3>
                <p className="text-sm text-red-800 mb-4">{registrationData.rejectionReason}</p>
                <div className="flex gap-3">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={() => navigate('/team-registration-form', { state: { registrationId } })}>
                    Resubmit Registration
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ArrowLeft"
                    onClick={() => navigate('/registration-dashboard')}>
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Indicator - Hidden in viewMode */}
        {!viewMode && (
          <div className="container mx-auto px-4 py-4">
            <RegistrationProgressIndicator customSteps={progressSteps} />
          </div>
        )}

        {/* Main Content */}
        <main className="container mx-auto px-4 py-6 lg:py-8">
          {viewMode ? (
            /* View Mode - Tabs Layout */
            <div className="space-y-6">
              {/* Tabs */}
              <div className="border-b border-border overflow-x-auto">
                <nav className="flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0" aria-label="Tabs">
                  {[
                    { id: 'declaration', label: 'Declaration', icon: FileText },
                    { id: 'team', label: 'Team Registration', icon: School },
                    { id: 'players', label: 'Player Registration', icon: Users },
                    { id: 'documents', label: 'Documents', icon: FileText }
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          flex items-center gap-1 sm:gap-2 py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap touch-target
                          ${activeTab === tab.id
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                          }
                        `}
                      >
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              {loading ? (
                <div className="card text-center py-8">
                  <p className="text-muted-foreground">Loading registration data...</p>
                </div>
              ) : (
                <div className="card">
                  {activeTab === 'declaration' && declarationData && (
                    <div className="space-y-6">
                      <DeclarationPreview
                        declarationData={declarationData}
                        onDownload={handleDownloadDeclaration}
                        viewMode={true} />
                      {uploadedDeclaration && (
                        <SignedDeclarationUpload
                          uploadedFile={uploadedDeclaration}
                          registrationId={registrationId}
                          viewMode={true} />
                      )}
                    </div>
                  )}
                  {activeTab === 'team' && teamData && (
                    <TeamOverviewTab data={teamData} />
                  )}
                  {activeTab === 'players' && playerData.length > 0 && (
                    <PlayerDetailsTab players={playerData} />
                  )}
                  {activeTab === 'documents' && (
                    <DocumentsTab documents={documentData} userRole="user" />
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Edit Mode - Original Layout */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Declaration Preview & Upload */}
              <div className="lg:col-span-2 space-y-6">
                {loading ? (
                  <div className="card text-center py-8">
                    <p className="text-muted-foreground">Loading registration data...</p>
                  </div>
                ) : (
                  <>
                    {/* Resubmission Warning */}
                    {isResubmission && !uploadedDeclaration && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-amber-900 mb-2">
                            Declaration Needs to be Re-uploaded
                          </h3>
                          <p className="text-xs text-amber-800 mb-2">
                            Since you've made changes to the registration, please download the updated declaration PDF above, 
                            sign it with the latest information, and upload the new signed declaration.
                          </p>
                          <p className="text-xs text-amber-800">
                            The previous signed declaration has been cleared because the registration data was updated.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Declaration Preview */}
                    {declarationData && (
                      <DeclarationPreview
                        declarationData={declarationData}
                        onDownload={handleDownloadDeclaration} />
                    )}

                    {/* Signed Declaration Upload - Moved up for better visibility */}
                    <div id="upload-section">
                      <SignedDeclarationUpload
                        onFileUpload={handleFileUpload}
                        uploadedFile={uploadedDeclaration}
                        onRemoveFile={handleRemoveFile}
                        registrationId={registrationId}
                        onUploadComplete={handleUploadComplete}
                        onUploadError={handleUploadError} />
                    </div>

                    {/* Captain Selection */}
                    <div id="captain-section">
                      {declarationData?.players && declarationData.players.length > 0 ? (
                        <CaptainSelection
                          players={declarationData.players}
                          selectedCaptain={selectedCaptain}
                          onCaptainSelect={handleCaptainSelect} />
                      ) : (
                        <div className="card bg-warning/10 border border-warning/20">
                          <div className="flex items-center gap-3">
                            <AlertCircle size={20} className="text-warning" />
                            <div>
                              <p className="font-medium text-foreground">No Players Found</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Please register players first before selecting a captain.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Right Column - Checklist & Submission */}
              <div className="space-y-6">
                {/* Registration Checklist */}
                <RegistrationChecklist checklistItems={checklistItems} />

                {/* Final Submission Panel */}
                {registrationData && (
                  <FinalSubmissionPanel
                    isReadyToSubmit={isReadyToSubmit}
                    onSubmit={handleFinalSubmit}
                    registrationData={registrationData}
                    registrationId={registrationId} />
                )}
              </div>
            </div>
          )}

          {/* Mobile Action Buttons */}
          {!viewMode && (
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 shadow-lg">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Save"
                  onClick={() => alert('Draft saved successfully')}
                  fullWidth>
                  Save Draft
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  iconName="Home"
                  onClick={() => navigate('/registration-dashboard')}
                  fullWidth>
                  Dashboard
                </Button>
              </div>
            </div>
          )}
        </main>

        {/* Bottom Spacing for Mobile */}
        <div className="h-24 lg:hidden" />
      </div>
    </AuthenticationGuard>);

};

export default DeclarationAndSubmission;